const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  "utm_creative",
  "utm_adgroup",
  "gclid",
  "gclsrc",
  "gad_source",
  "gad_campaignid",
  "gbraid",
  "wbraid",
  "kw",
  "cpn",
  "fbclid",
  "msclkid",
  "ttclid",
  "li_fat_id",
  "twclid",
] as const;

/**
 * Keys that mark a new ad click or campaign. When the address has one, every
 * stored UTM and click ID is dropped before the new ones are saved, so an old
 * Google gclid or utm_source=google never rides along on a later Bing click
 * (or the reverse). The same list is in utm-inline-script.ts.
 */
const NEW_TOUCH_KEYS = ["utm_source", "utm_medium", "gclid", "gbraid", "wbraid", "msclkid"];

/** Stored values with every UTM and click ID removed when `captured` starts a new touch. */
function withoutOldTouch(stored: UtmData, captured: UtmData): UtmData {
  if (!NEW_TOUCH_KEYS.some((k) => captured[k])) return stored;
  const kept: UtmData = {};
  for (const [k, v] of Object.entries(stored)) {
    if (!(UTM_KEYS as readonly string[]).includes(k)) kept[k] = v;
  }
  return kept;
}

const STORAGE_KEY = "restore_utm";
const COOKIE_NAME = "restore_utm";
const COOKIE_DAYS = 30;
const TARGET_HOST = "restorehair.com";

type UtmData = Record<string, string>;

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  const domain =
    typeof window !== "undefined" && window.location.hostname.endsWith(TARGET_HOST)
      ? `; domain=.${TARGET_HOST}`
      : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${domain}`;
}

/** decodeURIComponent that never throws on malformed "%" sequences. */
function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? safeDecode(match[1] ?? "") : null;
}

function readStored(): UtmData {
  if (typeof window === "undefined") return {};
  try {
    const ls = window.localStorage.getItem(STORAGE_KEY);
    if (ls) return JSON.parse(ls) as UtmData;
  } catch {
    /* ignore */
  }
  const cookie = readCookie(COOKIE_NAME);
  if (cookie) {
    try {
      return JSON.parse(cookie) as UtmData;
    } catch {
      /* ignore */
    }
  }
  return {};
}

function writeStored(data: UtmData) {
  if (typeof window === "undefined") return;
  const value = JSON.stringify(data);
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
  setCookie(COOKIE_NAME, value, COOKIE_DAYS);
}

function captureFromUrl(): UtmData {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const captured: UtmData = {};
  UTM_KEYS.forEach((k) => {
    if (params.has(k)) {
      captured[k] = params.get(k) ?? "";
    }
  });
  return captured;
}

function appendUtms(url: string, utms: UtmData): string {
  if (!Object.keys(utms).length) return url;
  try {
    const u = new URL(url, window.location.origin);
    const sameOrigin = u.origin === window.location.origin;
    if (!u.hostname.endsWith(TARGET_HOST) && !sameOrigin) return url;
    // For internal links we only carry the click IDs + UTMs (a backup for
    // when session storage / cookies get wiped); external links get all.
    const entries = sameOrigin
      ? Object.entries(utms).filter(([k]) =>
          (UTM_KEYS as readonly string[]).includes(k),
        )
      : Object.entries(utms);
    if (!entries.length) return url;
    entries.forEach(([k, v]) => {
      if (!u.searchParams.has(k)) u.searchParams.set(k, v);
    });
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * GA Connector writes its values into hidden inputs / cookies named
 * `gaconnector_*`. Collect whatever is present so the Salesforce payload can
 * carry them, and fill a few basics ourselves when GA Connector is absent.
 */
export function getGaconnectorParams(): UtmData {
  if (typeof window === "undefined") return {};
  const out: UtmData = {};

  // Cookies set by the GA Connector script.
  document.cookie.split(";").forEach((pair) => {
    const [rawKey, ...rest] = pair.split("=");
    const key = (rawKey ?? "").trim();
    if (!key.startsWith("gaconnector_")) return;
    const value = safeDecode(rest.join("=") || "");
    if (value) out[key] = value.slice(0, 600);
  });

  // Values held by the GA Connector script itself (it may not write every
  // field to a cookie when loaded after consent).
  try {
    const gac = (window as unknown as { gaconnector?: { getCookieValues?: () => Record<string, unknown> } }).gaconnector;
    const vals = typeof gac?.getCookieValues === "function" ? gac.getCookieValues() : null;
    if (vals && typeof vals === "object") {
      for (const [k, v] of Object.entries(vals)) {
        if (v === undefined || v === null || v === "") continue;
        const key = k.startsWith("gaconnector_") ? k : `gaconnector_${k}`;
        out[key] = String(v).slice(0, 600);
      }
    }
  } catch {
    /* ignore */
  }

  // Hidden inputs GA Connector populates on the page.
  document
    .querySelectorAll<HTMLInputElement>('input[name^="gaconnector_"]')
    .forEach((el) => {
      if (el.value) out[el.name] = el.value.slice(0, 600);
    });

  // Sensible fallbacks so the lead is never blank on these.
  if (!out["gaconnector_browser"]) out["gaconnector_browser"] = navigator.userAgent.slice(0, 300);
  if (!out["gaconnector_time_zone"]) {
    try {
      out["gaconnector_time_zone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      /* ignore */
    }
  }
  if (!out["gaconnector_lc_landing"] && !readStored()["gaconnector_lc_landing"]) {
    out["gaconnector_lc_landing"] = window.location.href.slice(0, 600);
  }
  // Only an EXTERNAL referrer is meaningful — an internal page-to-page
  // referrer would overwrite the real (ad / organic) source mid-funnel.
  const externalRef = externalReferrer();
  if (!out["gaconnector_lc_referrer"] && externalRef) {
    out["gaconnector_lc_referrer"] = externalRef;
  }
  return out;
}

/**
 * True when this page was opened from another page of this site. The site
 * copies click IDs and UTMs onto its own links, so such a page can carry a
 * gclid without being a new arrival.
 */
function internalReferrer(): boolean {
  if (typeof document === "undefined" || !document.referrer) return false;
  try {
    return new URL(document.referrer).origin === window.location.origin;
  } catch {
    return false;
  }
}

/** document.referrer, but only when it comes from another site. */
function externalReferrer(): string {
  if (typeof document === "undefined" || !document.referrer) return "";
  try {
    const u = new URL(document.referrer);
    if (u.origin === window.location.origin) return "";
    return document.referrer.slice(0, 600);
  } catch {
    return "";
  }
}

/**
 * Returns every tracking param we know about: values currently in the URL
 * merged over anything previously captured (localStorage / cookie).
 */
export function getTrackingParams(): UtmData {
  if (typeof window === "undefined") return {};
  const captured = captureFromUrl();
  return { ...withoutOldTouch(readStored(), captured), ...getGaconnectorParams(), ...captured };
}

export function initUtmTracking() {
  if (typeof window === "undefined") return;

  // Capture UTMs from the current URL and merge with stored values.
  const captured = captureFromUrl();
  const existing = withoutOldTouch(readStored(), captured);
  const merged = { ...existing, ...captured };

  // Persist first/last click referrer + content so they survive the
  // multi-step funnel (internal navigation wipes document.referrer).
  const ref = externalReferrer();
  if (ref) {
    merged["gaconnector_lc_referrer"] = ref;
    if (!merged["gaconnector_fc_referrer"]) merged["gaconnector_fc_referrer"] = ref;
  }
  const content =
    captured["utm_content"] || captured["utm_creative"] || captured["utm_adgroup"];
  if (content) {
    merged["gaconnector_lc_content"] = content;
    if (!merged["gaconnector_fc_content"]) merged["gaconnector_fc_content"] = content;
  }
  applyFirstPartyTouch(merged, captured, ref, internalReferrer());
  if (Object.keys(merged).length) writeStored(merged);

  // eslint-disable-next-line no-console
  console.info("[utm] init", { captured, stored: merged });

  // Proactively decorate every eligible link in the DOM so that middle-click,
  // Any link we decorate gets rel="nofollow" so a shared/leaked parameter URL
  // never passes crawl signals or re-attributes organic traffic as paid.
  const markNofollow = (a: HTMLAnchorElement) => {
    const rel = (a.getAttribute("rel") || "").split(/\s+/).filter(Boolean);
    if (!rel.includes("nofollow")) {
      rel.push("nofollow");
      a.setAttribute("rel", rel.join(" "));
    }
  };

  // right-click > "open in new tab", copy-link, and prefetch all carry the
  // click IDs / UTMs — those never fire a normal click event.
  const decorateAll = () => {
    const stored = readStored();
    if (!Object.keys(stored).length) return;
    document.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((a) => {
      const raw = a.getAttribute("href") || "";
      if (!raw || raw.startsWith("#") || /^(mailto:|tel:|javascript:)/i.test(raw)) return;
      const next = appendUtms(a.href, stored);
      if (next !== a.href) {
        a.setAttribute("href", next);
        markNofollow(a);
      }
    });
  };

  decorateAll();
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      decorateAll();
    });
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["href"],
  });

  // Intercept clicks on outbound links going to restorehair.com and append UTMs.
  const handler = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const anchor = target.closest("a") as HTMLAnchorElement | null;
    if (!anchor || !anchor.href) return;
    let url: URL;
    try {
      url = new URL(anchor.href);
    } catch {
      return;
    }
    if (!url.hostname.endsWith(TARGET_HOST) && url.origin !== window.location.origin)
      return;
    const stored = readStored();
    if (!Object.keys(stored).length) return;
    const next = appendUtms(anchor.href, stored);
    if (next !== anchor.href) {
      anchor.href = next;
      markNofollow(anchor);
      // eslint-disable-next-line no-console
      console.info("[utm] rewrote outbound link ->", next);
    }
  };

  // Phone taps. A tel: click opens the dialer and leaves no other trace, so
  // send one Zaraz event per tap with the page and the stored campaign. Zaraz
  // forwards it to GA4 (and to Google Ads once a "Phone tap" action exists).
  // No click ID or personal data is sent, only campaign-level fields.
  // `location` says where the link sat: "bar" (mobile quote bar), "header"
  // (site header) or "body" (page content and footer). The flag stops a
  // second init from adding a second listener, which would count each tap
  // twice.
  const w = window as unknown as { __rhPhoneClickBound?: boolean };
  if (!w.__rhPhoneClickBound) {
    w.__rhPhoneClickBound = true;
    document.addEventListener(
      "click",
      (event) => {
        const a = (event.target as HTMLElement | null)?.closest?.('a[href^="tel:"]') as
          | HTMLAnchorElement
          | null;
        if (!a) return;
        try {
          const stored = readStored();
          const tagged = a.closest("[data-phone-location]")?.getAttribute("data-phone-location");
          const props: Record<string, string> = {
            phone_number: a.getAttribute("href")!.slice(4),
            page_path: window.location.pathname,
            location: tagged || (a.closest("[data-site-header]") ? "header" : "body"),
          };
          for (const k of ["utm_source", "utm_medium", "utm_campaign", "cpn"]) {
            if (stored[k]) props[k] = String(stored[k]);
          }
          if (stored["gclid"] || stored["gbraid"] || stored["wbraid"]) props.paid_click = "1";
          const z = (window as unknown as { zaraz?: { track?: (n: string, p: unknown) => void } })
            .zaraz;
          z?.track?.("phone_click", props);
        } catch {
          /* tracking must never block the call */
        }
      },
      true,
    );
  }

  document.addEventListener("click", handler, true);
  document.addEventListener("auxclick", handler, true);
  document.addEventListener("contextmenu", handler as EventListener, true);
  document.addEventListener("pointerdown", handler as EventListener, true);

  // Also handle form submissions targeting restorehair.com
  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target as HTMLFormElement | null;
      if (!form || !form.action) return;
      let url: URL;
      try {
        url = new URL(form.action);
      } catch {
        return;
      }
      if (!url.hostname.endsWith(TARGET_HOST)) return;
      const stored = readStored();
      if (!Object.keys(stored).length) return;
      const next = appendUtms(form.action, stored);
      if (next !== form.action) form.action = next;
    },
    true,
  );
}
// Channel names match the First-Click Channel values GA Connector already
// writes, as Power BI showed them on 1 Oct 2026: Paid Search, Organic Search,
// Direct, Email, Referral, Organic Social, Organic Video, AI Assistant,
// Unassigned. Paid Social and Display are the GA4 names for those cases.
const SEARCH_HOSTS = /(^|\.)(google|bing|yahoo|duckduckgo|baidu|yandex|ecosia|aol|ask|brave|startpage|qwant)\./i;
// Checked before search: gemini.google.com and copilot.microsoft.com are AI
// assistants, not search result pages.
const AI_HOSTS = /(^|\.)(chatgpt\.com|chat\.openai\.com|openai\.com|perplexity\.ai|gemini\.google\.com|bard\.google\.com|copilot\.microsoft\.com|claude\.ai|you\.com|phind\.com|meta\.ai)$/i;
const VIDEO_HOSTS = /(^|\.)(youtube\.com|youtu\.be|vimeo\.com|tiktok\.com)$/i;
const SOCIAL_HOSTS = /(^|\.)(facebook\.com|fb\.com|m\.facebook\.com|l\.facebook\.com|lm\.facebook\.com|instagram\.com|l\.instagram\.com|t\.co|twitter\.com|x\.com|linkedin\.com|lnkd\.in|pinterest\.com|reddit\.com|snapchat\.com|threads\.net|nextdoor\.com)$/i;
const SOCIAL_SOURCES = /^(facebook|fb|instagram|ig|meta|twitter|x|linkedin|pinterest|reddit|snapchat|tiktok|youtube|threads)$/i;

/**
 * The short source name GA Connector and GA4 use for a search engine's host
 * ("www.bing.com" -> "bing"), so one engine is not split across "bing" and
 * "bing.com" in Salesforce. Empty for other hosts and for AI assistants on a
 * search engine's domain (gemini.google.com, copilot.microsoft.com).
 */
function searchEngineName(host: string): string {
  if (AI_HOSTS.test(host)) return "";
  return host.match(SEARCH_HOSTS)?.[2]?.toLowerCase() ?? "";
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function channelFor(source: string, medium: string, c: UtmData, refHost: string): string {
  const m = medium.toLowerCase();
  const src = source.toLowerCase();
  const socialSource = SOCIAL_SOURCES.test(src) || SOCIAL_HOSTS.test(src) || VIDEO_HOSTS.test(src);
  if (c["gclid"] || c["gbraid"] || c["wbraid"] || c["msclkid"]) return "Paid Search";
  if (c["fbclid"] && /^(cpc|ppc|paid)/.test(m)) return "Paid Social";
  if (/paid.?social|^social.?paid$/.test(m)) return "Paid Social";
  if (/^(cpc|ppc|paid|paidsearch|paid_search|sem)$/.test(m)) return socialSource ? "Paid Social" : "Paid Search";
  if (c["ttclid"] || c["li_fat_id"] || c["twclid"]) return "Paid Social";
  if (/^e-?mail$/.test(m) || src === "email" || src === "newsletter") return "Email";
  if (m === "display" || m === "banner" || m === "cpm") return "Display";
  if (AI_HOSTS.test(refHost) || AI_HOSTS.test(src) || /^(chatgpt|openai|perplexity|gemini|copilot|claude)/.test(src)) return "AI Assistant";
  if (VIDEO_HOSTS.test(refHost) || /^(youtube|vimeo|tiktok)$/.test(src) || m === "video") return "Organic Video";
  if (m === "social" || socialSource || SOCIAL_HOSTS.test(refHost)) return "Organic Social";
  if (m === "organic" || SEARCH_HOSTS.test(refHost)) return "Organic Search";
  if (m === "referral" || refHost) return "Referral";
  if (src === "(direct)") return "Direct";
  return "Unassigned";
}

/**
 * First-party first/last-click attribution. GA Connector's own script is held
 * by consent mode until the visitor interacts, so it often hasn't written its
 * gaconnector_* cookies before a lead submits. We record the same first/last
 * click fields ourselves; real GA Connector values (cookies / hidden inputs)
 * still take priority when present.
 *
 * A new "touch" is an arrival from another site (external referrer) or the
 * very first visit. Internal page-to-page navigation never overwrites it.
 */
function applyFirstPartyTouch(merged: UtmData, captured: UtmData, externalRef: string, fromOwnSite = false) {
  const hasStoredTouch = Boolean(merged["gaconnector_lc_source"]);
  // A page opened from another page of this site is not a new touch, even
  // with a gclid in its address: the site's link decoration put it there. On
  // 1 Oct 2026 the channel matrix found every paid and email lead recorded the
  // quote page, not the ad landing page, as the last-click landing page.
  if (hasStoredTouch && fromOwnSite) return;
  const hasCampaign = Boolean(
    captured["utm_source"] ||
    captured["gclid"] ||
    captured["gbraid"] ||
    captured["wbraid"] ||
    captured["msclkid"],
  );
  // A campaign-tagged landing (utm/gclid/msclkid) is always a new touch, even
  // with no referrer (pasted links, apps, some ad clicks).
  if (hasStoredTouch && !externalRef && !hasCampaign) return;

  const refHost = externalRef ? hostOf(externalRef) : "";
  let source = captured["utm_source"] || "";
  let medium = captured["utm_medium"] || "";
  if (!source && (captured["gclid"] || captured["gbraid"] || captured["wbraid"])) {
    source = "google";
    medium = medium || "cpc";
  }
  // About 1% of Bing ad clicks land with an msclkid and no UTMs.
  if (!source && captured["msclkid"]) {
    source = "bing";
    medium = medium || "cpc";
  }
  if (!source && refHost) {
    source = searchEngineName(refHost) || refHost;
    medium = medium || (SEARCH_HOSTS.test(refHost) ? "organic" : "referral");
  }
  if (!source && !hasCampaign) {
    source = "(direct)";
    medium = "(none)";
  }

  const touch: UtmData = {
    source,
    medium,
    campaign: captured["utm_campaign"] || "",
    term: captured["utm_term"] || captured["kw"] || "",
    content: captured["utm_content"] || captured["utm_creative"] || captured["utm_adgroup"] || "",
    landing: window.location.href.slice(0, 600),
    channel: channelFor(source, medium, captured, refHost),
  };
  const firstIsSet = Boolean(merged["gaconnector_fc_source"]);
  for (const [k, v] of Object.entries(touch)) {
    if (v) merged[`gaconnector_lc_${k}`] = v;
    else delete merged[`gaconnector_lc_${k}`];
    if (!firstIsSet && v) merged[`gaconnector_fc_${k}`] = v;
  }
}
